import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

interface UTMParams {
  timeEnd: number;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
}

const UTM_STORAGE_KEY = 'UTMParams';
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
const MAX_RETRIES = 50;
const RETRY_INTERVAL = 100;

/**
 * UTM パラメータトラッカーコンポーネント
 *
 * @element kd-utm-tracker
 * @description 会員登録ページで UTM パラメータを取得し、Custom Fields App の DOM 要素にセットする。
 *              URL に UTM パラメータがある場合は localStorage に保存（月末期限）。
 *              DOM 要素は Custom Fields App が非同期で render するため、retry パターンで待機する。
 */
@customElement('kd-utm-tracker')
export class KdUtmTracker extends LitElement {
  createRenderRoot() {
    return this;
  }

  private retryTimer: ReturnType<typeof setInterval> | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.startTracking();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopRetry();
  }

  private stopRetry(): void {
    if (this.retryTimer !== null) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private startTracking(): void {
    let retryCount = 0;

    this.retryTimer = setInterval(() => {
      const sourceEl = document.getElementById('customer-utm-source');
      const noteSourceEl = document.getElementById('customer-note-utm-source');

      if (sourceEl || noteSourceEl) {
        this.applyUTMParams();
        this.stopRetry();
        return;
      }

      retryCount++;
      if (retryCount >= MAX_RETRIES) {
        this.stopRetry();
      }
    }, RETRY_INTERVAL);
  }

  private applyUTMParams(): void {
    const params = new URLSearchParams(window.location.search);
    const urlSource = params.get('utm_source') || '';
    const urlMedium = params.get('utm_medium') || '';
    const urlCampaign = params.get('utm_campaign') || '';
    const now = new Date();

    if (urlSource || urlMedium || urlCampaign) {
      this.setFieldValues(urlSource, urlMedium, urlCampaign);

      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const stored: UTMParams = {
        timeEnd: endOfMonth.getTime(),
        utm_source: urlSource,
        utm_medium: urlMedium,
        utm_campaign: urlCampaign,
      };
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(stored));
    } else {
      const raw = localStorage.getItem(UTM_STORAGE_KEY);
      if (!raw) return;

      const stored: UTMParams = JSON.parse(raw);
      if (now.getTime() > stored.timeEnd) {
        localStorage.removeItem(UTM_STORAGE_KEY);
      } else if (stored.utm_source || stored.utm_medium || stored.utm_campaign) {
        this.setFieldValues(stored.utm_source, stored.utm_medium, stored.utm_campaign);
      }
    }
  }

  private setFieldValues(source: string, medium: string, campaign: string): void {
    const values: Record<(typeof UTM_FIELDS)[number], string> = {
      utm_source: source,
      utm_medium: medium,
      utm_campaign: campaign,
    };

    for (const field of UTM_FIELDS) {
      const el = document.getElementById(`customer-${field.replace('_', '-')}`) as HTMLInputElement | null;
      if (el) {
        el.value = values[field];
      }

      const noteEl = document.getElementById(`customer-note-${field.replace('_', '-')}`) as HTMLInputElement | null;
      if (noteEl) {
        noteEl.disabled = false;
        noteEl.value = values[field];
      }
    }
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-utm-tracker': KdUtmTracker;
  }
}
